/**
 * Dialog de configuração do node "Criar Tarefa" (task_create)
 *
 * CHANGELOG:
 *   v1.2.0 - 2026-09-07 - Variável de saída migrada para o modal de destino.
 *   v1.1.0 - 2026-07-09 - Adicionado campo opcional "Template de Checklist".
 *                         Quando selecionado, o engine aplica os grupos/itens do
 *                         template na tarefa recém-criada via RPC
 *                         apply_task_checklist_template (mode='append').
 *   v1.0.0 - 2026-05-31 - Versão inicial.
 *
 * Permite configurar destino (categoria/fase/classificação), título, descrição,
 * prioridade, vencimento, responsável, vínculos com contato/negócio, template
 * de checklist opcional e variável de saída com o task_id criado.
 */


import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
// Inputs ricos do Flow Builder: variável global + emoji + formatação WhatsApp
import { InputWithVariables } from '../InputWithVariables';
import { TextareaWithVariables } from '../TextareaWithVariables';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import {
  ListTodo, MapPin, Type, AlignLeft, Flag, Calendar,
  User, Link2, ArrowRight, HelpCircle, Zap, ListChecks,
} from 'lucide-react';

import { FlowNode, TaskCreateConfig } from '@/types/flows';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/lib/toast';
import { useTaskChecklistTemplatesList } from '@/hooks/useTaskChecklistTemplates';

import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariablePickerCombobox } from '../VariablePickerCombobox';
import { VariableDestinationPicker } from '../VariableDestinationPicker';
import { SystemUserSelector } from '@/components/crm/SystemUserSelector';

interface ConfigureTaskCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: TaskCreateConfig) => void;
}

export function ConfigureTaskCreateDialog({
  open,
  onOpenChange,
  node,
  onSave,
}: ConfigureTaskCreateDialogProps) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id || '';
  const nicknameRef = useRef<NodeIdFieldRef>(null);

  const existing = node.data.config as TaskCreateConfig | undefined;

  // ============== Estado: destino ==============
  const [categoryId, setCategoryId] = useState(existing?.category_id || '');
  const [categoryName, setCategoryName] = useState(existing?.category_name || '');
  const [categoryColor, setCategoryColor] = useState(existing?.category_color || '');
  const [phaseId, setPhaseId] = useState(existing?.phase_id || '');
  const [phaseName, setPhaseName] = useState(existing?.phase_name || '');
  const [phaseColor, setPhaseColor] = useState(existing?.phase_color || '');
  const [classificationId, setClassificationId] = useState(existing?.classification_id || '');
  const [classificationName, setClassificationName] = useState(existing?.classification_name || '');
  const [classificationColor, setClassificationColor] = useState(existing?.classification_color || '');

  // ============== Estado: título ==============
  const [titleMode, setTitleMode] = useState<TaskCreateConfig['title_mode']>(existing?.title_mode || 'auto');
  const [titleStatic, setTitleStatic] = useState(existing?.title_static || '');
  const [titleVariable, setTitleVariable] = useState(existing?.title_variable || '');
  const [titleVariableId, setTitleVariableId] = useState(existing?.title_variable_id || '');

  // ============== Estado: descrição ==============
  const [descriptionMode, setDescriptionMode] = useState<NonNullable<TaskCreateConfig['description_mode']>>(
    existing?.description_mode || 'none'
  );
  const [descriptionStatic, setDescriptionStatic] = useState(existing?.description_static || '');
  const [descriptionVariable, setDescriptionVariable] = useState(existing?.description_variable || '');
  const [descriptionVariableId, setDescriptionVariableId] = useState(existing?.description_variable_id || '');

  // ============== Estado: prioridade ==============
  // Normaliza prioridade legada 'medium' -> 'normal' (enum task_priority do banco)
  const normalizePriority = (p: any): NonNullable<TaskCreateConfig['priority']> =>
    p === 'medium' ? 'normal' : ((p as NonNullable<TaskCreateConfig['priority']>) || 'normal');
  const [priority, setPriority] = useState<NonNullable<TaskCreateConfig['priority']>>(normalizePriority(existing?.priority));

  // ============== Estado: vencimento ==============
  const [dueDateMode, setDueDateMode] = useState<NonNullable<TaskCreateConfig['due_date_mode']>>(
    existing?.due_date_mode || 'none'
  );
  const [dueRelativeValue, setDueRelativeValue] = useState<number>(existing?.due_date_relative_value || 1);
  const [dueRelativeUnit, setDueRelativeUnit] = useState<NonNullable<TaskCreateConfig['due_date_relative_unit']>>(
    existing?.due_date_relative_unit || 'days'
  );
  const [dueVariable, setDueVariable] = useState(existing?.due_date_variable || '');
  const [dueVariableId, setDueVariableId] = useState(existing?.due_date_variable_id || '');

  // ============== Estado: responsável ==============
  const [assigneeMode, setAssigneeMode] = useState<NonNullable<TaskCreateConfig['assignee_mode']>>(
    existing?.assignee_mode || 'none'
  );
  const [assigneeId, setAssigneeId] = useState(existing?.assignee_id || '');
  const [assigneeName, setAssigneeName] = useState(existing?.assignee_name || '');
  const [assigneeVariable, setAssigneeVariable] = useState(existing?.assignee_variable || '');
  const [assigneeVariableId, setAssigneeVariableId] = useState(existing?.assignee_variable_id || '');

  // ============== Estado: vínculos ==============
  const [linkToContact, setLinkToContact] = useState<boolean>(existing?.link_to_contact ?? true);
  const [linkToDeal, setLinkToDeal] = useState<NonNullable<TaskCreateConfig['link_to_deal']>>(
    existing?.link_to_deal || 'from_trigger'
  );
  const [dealVariable, setDealVariable] = useState(existing?.deal_variable || '');
  const [dealVariableId, setDealVariableId] = useState(existing?.deal_variable_id || '');

  // ============== Estado: saída ==============
  const [outputVariable, setOutputVariable] = useState(existing?.output_variable || '');
  const [outputVariableId, setOutputVariableId] = useState(existing?.output_variable_id || '');

  // ============== Estado: template de checklist (opcional) ==============
  const [checklistTemplateId, setChecklistTemplateId] = useState(existing?.checklist_template_id || '');
  const [checklistTemplateName, setChecklistTemplateName] = useState(existing?.checklist_template_name || '');


  const [showHelp, setShowHelp] = useState(false);

  // Sincroniza estado quando o node muda ou dialog reabre
  useEffect(() => {
    if (!open) return;
    const cfg = node.data.config as TaskCreateConfig | undefined;
    setCategoryId(cfg?.category_id || '');
    setCategoryName(cfg?.category_name || '');
    setCategoryColor(cfg?.category_color || '');
    setPhaseId(cfg?.phase_id || '');
    setPhaseName(cfg?.phase_name || '');
    setPhaseColor(cfg?.phase_color || '');
    setClassificationId(cfg?.classification_id || '');
    setClassificationName(cfg?.classification_name || '');
    setClassificationColor(cfg?.classification_color || '');
    setTitleMode(cfg?.title_mode || 'auto');
    setTitleStatic(cfg?.title_static || '');
    setTitleVariable(cfg?.title_variable || '');
    setTitleVariableId(cfg?.title_variable_id || '');
    setDescriptionMode(cfg?.description_mode || 'none');
    setDescriptionStatic(cfg?.description_static || '');
    setDescriptionVariable(cfg?.description_variable || '');
    setDescriptionVariableId(cfg?.description_variable_id || '');
    setPriority(normalizePriority(cfg?.priority));
    setDueDateMode(cfg?.due_date_mode || 'none');
    setDueRelativeValue(cfg?.due_date_relative_value || 1);
    setDueRelativeUnit(cfg?.due_date_relative_unit || 'days');
    setDueVariable(cfg?.due_date_variable || '');
    setDueVariableId(cfg?.due_date_variable_id || '');
    setAssigneeMode(cfg?.assignee_mode || 'none');
    setAssigneeId(cfg?.assignee_id || '');
    setAssigneeName(cfg?.assignee_name || '');
    setAssigneeVariable(cfg?.assignee_variable || '');
    setAssigneeVariableId(cfg?.assignee_variable_id || '');
    setLinkToContact(cfg?.link_to_contact ?? true);
    setLinkToDeal(cfg?.link_to_deal || 'from_trigger');
    setDealVariable(cfg?.deal_variable || '');
    setDealVariableId(cfg?.deal_variable_id || '');
    setOutputVariable(cfg?.output_variable || '');
    setOutputVariableId(cfg?.output_variable_id || '');
    setChecklistTemplateId(cfg?.checklist_template_id || '');
    setChecklistTemplateName(cfg?.checklist_template_name || '');

  }, [open, node.id]);

  // Query: categorias ativas do tenant
  const { data: categories, isLoading: loadingCategories } = useQuery({
    queryKey: ['flow-task-categories', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_categories')
        .select('id, name, color')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && open,
  });

  // Query: fases ativas da categoria selecionada
  const { data: phases, isLoading: loadingPhases } = useQuery({
    queryKey: ['flow-task-phases', tenantId, categoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_phases')
        .select('id, name, color')
        .eq('tenant_id', tenantId)
        .eq('category_id', categoryId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && !!categoryId && open,
  });

  // Query: classificações ativas da categoria selecionada (opcional)
  const { data: classifications } = useQuery({
    queryKey: ['flow-task-classifications', tenantId, categoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_classifications')
        .select('id, name, color')
        .eq('tenant_id', tenantId)
        .eq('category_id', categoryId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && !!categoryId && open,
  });

  // Query: templates de checklist ativos (opcional) — mesmo hook do modal manual
  const { data: checklistTemplates, isLoading: loadingChecklistTemplates } =
    useTaskChecklistTemplatesList(true);



  // Handler: troca de categoria — limpa fase/classificação dependentes
  const handleCategoryChange = (id: string) => {
    const cat = categories?.find((c) => c.id === id);
    setCategoryId(id);
    setCategoryName(cat?.name || '');
    setCategoryColor(cat?.color || '');
    setPhaseId('');
    setPhaseName('');
    setPhaseColor('');
    setClassificationId('');
    setClassificationName('');
    setClassificationColor('');
  };

  const handlePhaseChange = (id: string) => {
    const p = phases?.find((x) => x.id === id);
    setPhaseId(id);
    setPhaseName(p?.name || '');
    setPhaseColor(p?.color || '');
  };

  const handleClassificationChange = (id: string) => {
    if (!id || id === '__none__') {
      setClassificationId('');
      setClassificationName('');
      setClassificationColor('');
      return;
    }
    const c = classifications?.find((x) => x.id === id);
    setClassificationId(id);
    setClassificationName(c?.name || '');
    setClassificationColor(c?.color || '');
  };

  // Validação leve do formulário
  const isFormValid = (): boolean => {
    if (!categoryId || !phaseId) return false;
    if (titleMode === 'static' && !titleStatic.trim()) return false;
    if (titleMode === 'from_variable' && !titleVariable.trim()) return false;
    if (descriptionMode === 'static' && !descriptionStatic.trim()) return false;
    if (descriptionMode === 'from_variable' && !descriptionVariable.trim()) return false;
    if (dueDateMode === 'relative' && (!dueRelativeValue || dueRelativeValue <= 0)) return false;
    if (dueDateMode === 'from_variable' && !dueVariable.trim()) return false;
    if (assigneeMode === 'manual' && !assigneeId) return false;
    if (assigneeMode === 'from_variable' && !assigneeVariable.trim()) return false;
    if (linkToDeal === 'from_variable' && !dealVariable.trim()) return false;
    return true;
  };

  const handleSave = () => {
    if (!isFormValid()) {
      showToast.error('Preencha todos os campos obrigatórios');
      return;
    }
    const config: TaskCreateConfig = {
      nickname: nicknameRef.current?.getNickname() || undefined,
      category_id: categoryId,
      category_name: categoryName || undefined,
      category_color: categoryColor || undefined,
      phase_id: phaseId,
      phase_name: phaseName || undefined,
      phase_color: phaseColor || undefined,
      classification_id: classificationId || undefined,
      classification_name: classificationName || undefined,
      classification_color: classificationColor || undefined,

      title_mode: titleMode,
      title_static: titleMode === 'static' ? titleStatic.trim() : undefined,
      title_variable: titleMode === 'from_variable' ? titleVariable.trim() : undefined,
      title_variable_id: titleMode === 'from_variable' ? titleVariableId || undefined : undefined,

      description_mode: descriptionMode,
      description_static: descriptionMode === 'static' ? descriptionStatic.trim() : undefined,
      description_variable: descriptionMode === 'from_variable' ? descriptionVariable.trim() : undefined,
      description_variable_id: descriptionMode === 'from_variable' ? descriptionVariableId || undefined : undefined,

      priority,

      due_date_mode: dueDateMode,
      due_date_relative_value: dueDateMode === 'relative' ? dueRelativeValue : undefined,
      due_date_relative_unit: dueDateMode === 'relative' ? dueRelativeUnit : undefined,
      due_date_variable: dueDateMode === 'from_variable' ? dueVariable.trim() : undefined,
      due_date_variable_id: dueDateMode === 'from_variable' ? dueVariableId || undefined : undefined,

      assignee_mode: assigneeMode,
      assignee_id: assigneeMode === 'manual' ? assigneeId : undefined,
      assignee_name: assigneeMode === 'manual' ? assigneeName || undefined : undefined,
      assignee_variable: assigneeMode === 'from_variable' ? assigneeVariable.trim() : undefined,
      assignee_variable_id: assigneeMode === 'from_variable' ? assigneeVariableId || undefined : undefined,

      link_to_contact: linkToContact,
      link_to_deal: linkToDeal,
      deal_variable: linkToDeal === 'from_variable' ? dealVariable.trim() : undefined,
      deal_variable_id: linkToDeal === 'from_variable' ? dealVariableId || undefined : undefined,

      output_variable: outputVariable.trim() || undefined,
      output_variable_id: outputVariableId || undefined,

      // Template de checklist opcional
      checklist_template_id: checklistTemplateId || undefined,
      checklist_template_name: checklistTemplateId ? checklistTemplateName || undefined : undefined,
    };


    onSave(node.id, config);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl h-[85vh] max-h-[85vh] flex flex-col overflow-hidden min-h-0">
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <ListTodo className="h-5 w-5 text-amber-600" />
                Configurar Criar Tarefa
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
            <DialogDescription>
              Cria uma tarefa no módulo Tarefas quando o fluxo passa por este node
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto pr-4">
            <div className="space-y-4 py-4">
              {/* Identificação */}
              <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

              {/* ===================== DESTINO ===================== */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                  <MapPin className="h-4 w-4" />
                  Destino da Tarefa
                </div>

                <div className="space-y-2">
                  <Label htmlFor="task-category">Categoria *</Label>
                  <Select value={categoryId} onValueChange={handleCategoryChange} disabled={loadingCategories}>
                    <SelectTrigger id="task-category">
                      <SelectValue placeholder={loadingCategories ? 'Carregando...' : 'Selecione a categoria'} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories?.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          <div className="flex items-center gap-2">
                            {c.color && (
                              <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: c.color }}
                              />
                            )}
                            <span>{c.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="task-phase">Fase inicial *</Label>
                  <Select
                    value={phaseId}
                    onValueChange={handlePhaseChange}
                    disabled={!categoryId || loadingPhases}
                  >
                    <SelectTrigger id="task-phase">
                      <SelectValue
                        placeholder={
                          !categoryId
                            ? 'Selecione a categoria primeiro'
                            : loadingPhases
                            ? 'Carregando...'
                            : 'Selecione a fase'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {phases?.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          <div className="flex items-center gap-2">
                            {p.color && (
                              <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: p.color }}
                              />
                            )}
                            <span>{p.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="task-classification">Classificação (opcional)</Label>
                  <Select
                    value={classificationId || '__none__'}
                    onValueChange={handleClassificationChange}
                    disabled={!categoryId}
                  >
                    <SelectTrigger id="task-classification">
                      <SelectValue placeholder="Sem classificação" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sem classificação</SelectItem>
                      {classifications?.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          <div className="flex items-center gap-2">
                            {c.color && (
                              <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: c.color }}
                              />
                            )}
                            <span>{c.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              {/* ===================== TÍTULO ===================== */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                  <Type className="h-4 w-4" />
                  Título da Tarefa
                </div>

                <RadioGroup
                  value={titleMode}
                  onValueChange={(v) => setTitleMode(v as TaskCreateConfig['title_mode'])}
                  className="space-y-2"
                >
                  <div className="flex items-start space-x-3 p-3 rounded-lg border border-border">
                    <RadioGroupItem value="auto" id="title-auto" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="title-auto" className="font-medium cursor-pointer">
                        Automático
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Gera "[Categoria] - DD/MM/YYYY"
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-3 rounded-lg border border-border">
                    <RadioGroupItem value="static" id="title-static" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="title-static" className="font-medium cursor-pointer">
                        Texto fixo
                      </Label>
                      <p className="text-xs text-muted-foreground">Aceita variáveis no formato {'{{nome}}'}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-3 rounded-lg border border-border">
                    <RadioGroupItem value="from_variable" id="title-var" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="title-var" className="font-medium cursor-pointer">
                        De uma variável
                      </Label>
                      <p className="text-xs text-muted-foreground">Usa o conteúdo da variável escolhida</p>
                    </div>
                  </div>
                </RadioGroup>

                {titleMode === 'static' && (
                  // Input rico: aceita inserção de variáveis globais via botão {}
                  <InputWithVariables
                    tenantId={tenantId}
                    value={titleStatic}
                    onChange={setTitleStatic}
                    placeholder="Ex.: Pós-venda {{contato_nome}}"
                    maxLength={200}
                  />
                )}

                {titleMode === 'from_variable' && (
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={titleVariableId || titleVariable}
                    onChange={(v) => {
                      if (v) {
                        setTitleVariableId(v.id);
                        setTitleVariable(v.name);
                      } else {
                        setTitleVariableId('');
                        setTitleVariable('');
                      }
                    }}
                    allowCreate={false}
                    allowClear={false}
                    placeholder="Selecione a variável"
                  />
                )}
              </div>

              <Separator />

              {/* ===================== DESCRIÇÃO ===================== */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                  <AlignLeft className="h-4 w-4" />
                  Descrição (opcional)
                </div>

                <Select
                  value={descriptionMode}
                  onValueChange={(v) => setDescriptionMode(v as NonNullable<TaskCreateConfig['description_mode']>)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem descrição</SelectItem>
                    <SelectItem value="static">Texto fixo</SelectItem>
                    <SelectItem value="from_variable">De uma variável</SelectItem>
                  </SelectContent>
                </Select>

                {descriptionMode === 'static' && (
                  // Textarea rica: variáveis, emojis e formatação WhatsApp (*negrito*, _itálico_, ~tachado~)
                  <TextareaWithVariables
                    tenantId={tenantId}
                    value={descriptionStatic}
                    onChange={setDescriptionStatic}
                    placeholder="Descrição da tarefa. Aceita {{variaveis}} e *negrito*, _itálico_, ~tachado~"
                    rows={5}
                    maxLength={2000}
                  />
                )}

                {descriptionMode === 'from_variable' && (
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={descriptionVariableId || descriptionVariable}
                    onChange={(v) => {
                      if (v) {
                        setDescriptionVariableId(v.id);
                        setDescriptionVariable(v.name);
                      } else {
                        setDescriptionVariableId('');
                        setDescriptionVariable('');
                      }
                    }}
                    allowCreate={false}
                    allowClear={false}
                    placeholder="Selecione a variável"
                  />
                )}
              </div>

              <Separator />

              {/* ===================== PRIORIDADE ===================== */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                  <Flag className="h-4 w-4" />
                  Prioridade
                </div>
                <Select
                  value={priority}
                  onValueChange={(v) => setPriority(v as NonNullable<TaskCreateConfig['priority']>)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baixa</SelectItem>
                    <SelectItem value="normal">Média</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* ===================== VENCIMENTO ===================== */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                  <Calendar className="h-4 w-4" />
                  Vencimento (opcional)
                </div>

                <Select
                  value={dueDateMode}
                  onValueChange={(v) => setDueDateMode(v as NonNullable<TaskCreateConfig['due_date_mode']>)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem vencimento</SelectItem>
                    <SelectItem value="relative">Daqui a X tempo (relativo a agora)</SelectItem>
                    <SelectItem value="from_variable">De uma variável (data ISO)</SelectItem>
                  </SelectContent>
                </Select>

                {dueDateMode === 'relative' && (
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      min={1}
                      value={dueRelativeValue}
                      onChange={(e) => setDueRelativeValue(Math.max(1, parseInt(e.target.value, 10) || 0))}
                    />
                    <Select
                      value={dueRelativeUnit}
                      onValueChange={(v) =>
                        setDueRelativeUnit(v as NonNullable<TaskCreateConfig['due_date_relative_unit']>)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="minutes">Minutos</SelectItem>
                        <SelectItem value="hours">Horas</SelectItem>
                        <SelectItem value="days">Dias</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {dueDateMode === 'from_variable' && (
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={dueVariableId || dueVariable}
                    onChange={(v) => {
                      if (v) {
                        setDueVariableId(v.id);
                        setDueVariable(v.name);
                      } else {
                        setDueVariableId('');
                        setDueVariable('');
                      }
                    }}
                    allowCreate={false}
                    allowClear={false}
                    placeholder="Variável com data ISO 8601"
                  />
                )}
              </div>

              <Separator />

              {/* ===================== RESPONSÁVEL ===================== */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                  <User className="h-4 w-4" />
                  Responsável
                </div>

                <Select
                  value={assigneeMode}
                  onValueChange={(v) => setAssigneeMode(v as NonNullable<TaskCreateConfig['assignee_mode']>)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem responsável</SelectItem>
                    <SelectItem value="manual">Usuário fixo</SelectItem>
                    <SelectItem value="deal_seller">Vendedor do negócio vinculado</SelectItem>
                    <SelectItem value="contact_seller">Vendedor padrão do contato</SelectItem>
                    <SelectItem value="from_variable">De uma variável (user_id)</SelectItem>
                  </SelectContent>
                </Select>

                {assigneeMode === 'manual' && (
                  <SystemUserSelector
                    value={assigneeId || null}
                    onSelect={(userId, user) => {
                      setAssigneeId(userId || '');
                      setAssigneeName(user?.full_name || user?.email || '');
                    }}
                    placeholder="Selecionar usuário..."
                  />
                )}

                {assigneeMode === 'from_variable' && (
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={assigneeVariableId || assigneeVariable}
                    onChange={(v) => {
                      if (v) {
                        setAssigneeVariableId(v.id);
                        setAssigneeVariable(v.name);
                      } else {
                        setAssigneeVariableId('');
                        setAssigneeVariable('');
                      }
                    }}
                    allowCreate={false}
                    allowClear={false}
                    placeholder="Variável com user_id"
                  />
                )}
              </div>

              <Separator />

              {/* ===================== VÍNCULOS ===================== */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                  <Link2 className="h-4 w-4" />
                  Vínculos
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                  <div className="space-y-0.5">
                    <Label className="font-medium">Vincular ao contato da conversa</Label>
                    <p className="text-xs text-muted-foreground">
                      Recomendado quando o fluxo é disparado por um chat
                    </p>
                  </div>
                  <Switch checked={linkToContact} onCheckedChange={setLinkToContact} />
                </div>

                <RadioGroup
                  value={linkToDeal}
                  onValueChange={(v) => setLinkToDeal(v as NonNullable<TaskCreateConfig['link_to_deal']>)}
                  className="space-y-2"
                >
                  <div className="flex items-start space-x-3 p-3 rounded-lg border border-border">
                    <RadioGroupItem value="from_trigger" id="deal-trigger" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="deal-trigger" className="font-medium cursor-pointer flex items-center gap-1.5">
                        <Zap className="h-3.5 w-3.5 text-amber-500" />
                        Negócio do gatilho CRM
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Usa o deal que disparou o fluxo (ex.: gatilho "Ganhou", "Mudou de fase")
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-3 rounded-lg border border-border">
                    <RadioGroupItem value="from_variable" id="deal-var" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="deal-var" className="font-medium cursor-pointer">
                        ID do deal em variável
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Use a variável criada por um node "Criar Negócio"
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-3 rounded-lg border border-border">
                    <RadioGroupItem value="none" id="deal-none" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="deal-none" className="font-medium cursor-pointer">
                        Sem vínculo com negócio
                      </Label>
                      <p className="text-xs text-muted-foreground">A tarefa não será associada a deal</p>
                    </div>
                  </div>
                </RadioGroup>

                {linkToDeal === 'from_variable' && (
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={dealVariableId || dealVariable}
                    onChange={(v) => {
                      if (v) {
                        setDealVariableId(v.id);
                        setDealVariable(v.name);
                      } else {
                        setDealVariableId('');
                        setDealVariable('');
                      }
                    }}
                    allowCreate={false}
                    allowClear={false}
                    placeholder="Variável com ID do deal"
                  />
                )}
              </div>

              <Separator />

              {/* ===================== TEMPLATE DE CHECKLIST (opcional) ===================== */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                  <ListChecks className="h-4 w-4" />
                  Template de Checklist (opcional)
                </div>
                <Label>Aplicar template de checklist na tarefa criada</Label>
                <Select
                  value={checklistTemplateId || '__none__'}
                  onValueChange={(v) => {
                    if (!v || v === '__none__') {
                      setChecklistTemplateId('');
                      setChecklistTemplateName('');
                      return;
                    }
                    const tpl = checklistTemplates?.find((t: any) => t.id === v);
                    setChecklistTemplateId(v);
                    setChecklistTemplateName(tpl?.name || '');
                  }}
                  disabled={loadingChecklistTemplates}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={loadingChecklistTemplates ? 'Carregando...' : 'Nenhum template'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhum template</SelectItem>
                    {(checklistTemplates || []).map((tpl: any) => (
                      <SelectItem key={tpl.id} value={tpl.id}>
                        <div className="flex flex-col">
                          <span>{tpl.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {tpl.group_count} listas · {tpl.item_count} itens
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!loadingChecklistTemplates && (!checklistTemplates || checklistTemplates.length === 0) && (
                  <p className="text-xs text-muted-foreground">
                    Nenhum template disponível. Cadastre em Configurações → Templates de Checklist.
                  </p>
                )}
                {checklistTemplateId && (
                  <p className="text-xs text-muted-foreground">
                    Os grupos e itens do template serão adicionados à tarefa criada (modo append).
                  </p>
                )}
              </div>

              <Separator />



              {/* ===================== SAÍDA ===================== */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                  <ArrowRight className="h-4 w-4" />
                  Saída (opcional)
                </div>
                <Label>Armazenar ID da tarefa em variável</Label>
                <VariableDestinationPicker
                  tenantId={tenantId}
                  value={outputVariableId || outputVariable}
                  valueName={outputVariable}
                  onChange={(v) => {
                    if (v) {
                      setOutputVariableId(v.id);
                      setOutputVariable(v.name);
                    } else {
                      setOutputVariableId('');
                      setOutputVariable('');
                    }
                  }}
                  allowClear
                  placeholder="Selecione ou crie uma variável"
                />
                <p className="text-xs text-muted-foreground">
                  Permite usar o ID da tarefa em nodes posteriores
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isFormValid()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType="task_create" />
    </>
  );
}
